import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import PageHeader from "components/ui/page-header";
import HasRolePermission from "components/shared/has-role-permission";
import { formatPhone } from "utils/misc";
import { fetchTeams, setTeamActive } from "actions/teams-admin";

// Plain-language explanation of what S3_Teams.sOutboundPhoneNumber controls.
// Traced from Helper/dbHelper.cs:2683-2765 (OutboundPhoneNumber(callerTeam) →
// Lookup): the team number is (a) the last fallback in the outbound caller-ID
// chain when we know the patient but no branch/company caller ID is configured,
// and (b) the caller ID used when a coach on the team places an outbound call
// to a number that doesn't match a known patient (PatientsSerializer.cs:3749).
const PHONE_BLURB =
  "Phone: fallback caller ID S3 uses when a coach on this team places an outbound call — used when the patient's branch or company doesn't have a caller ID set, or when calling a number that doesn't match a known patient.";

const styles = {
  page: { maxWidth: 1100, margin: "0 auto", padding: 20 },
  meta: { color: "#666", fontSize: 13, marginTop: 6 },
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 },
  row: {
    border: "1px solid #e0e0e0",
    borderRadius: 4,
    marginBottom: 8,
    background: "#fff"
  },
  rowHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 16px",
    cursor: "pointer",
    userSelect: "none"
  },
  rowHeaderInactive: { background: "#fafafa" },
  caret: { width: 12, display: "inline-block", textAlign: "center", color: "#888" },
  teamName: { fontWeight: 600, fontSize: 15 },
  teamNameInactive: { color: "#888" },
  teamMeta: { color: "#666", fontSize: 12, marginLeft: 8 },
  blurb: {
    color: "#555",
    fontSize: 12,
    background: "#f5f7fa",
    border: "1px solid #e0e4e8",
    borderRadius: 4,
    padding: "8px 12px",
    marginBottom: 12
  },
  spacer: { flex: 1 },
  pill: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
    marginLeft: 6
  },
  pillActive:   { background: "#e6f4ea", color: "#137333" },
  pillInactive: { background: "#fce8e6", color: "#a50e0e" },
  pillWarn:     { background: "#fef7e0", color: "#b06000" },
  button: { padding: "5px 12px", cursor: "pointer", fontSize: 13, marginLeft: 8 },
  buttonDisabled: { padding: "5px 12px", cursor: "not-allowed", fontSize: 13, marginLeft: 8, opacity: 0.5 },
  buttonDanger: {
    padding: "5px 12px",
    cursor: "pointer",
    fontSize: 13,
    marginLeft: 8,
    background: "#a50e0e",
    color: "#fff",
    border: "1px solid #7a0a0a",
    borderRadius: 3
  },
  cancelButton: {
    padding: "5px 12px",
    cursor: "pointer",
    fontSize: 13,
    marginLeft: 4,
    background: "transparent",
    border: "1px solid #ccc",
    borderRadius: 3,
    color: "#333"
  },
  panel: { borderTop: "1px solid #eee", padding: "12px 16px", background: "#fbfbfb" },
  panelHeader: {
    fontSize: 13,
    color: "#444",
    marginBottom: 10,
    display: "flex",
    alignItems: "center",
    gap: 8
  },
  panelHeaderText: { flex: 1 },
  panelSubheader: { fontSize: 12, color: "#666", marginTop: 8, marginBottom: 4 },
  chipRow: { display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 },
  chip: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 10,
    fontSize: 11,
    background: "#eef2f6",
    color: "#333"
  },
  companyList: { listStyle: "none", padding: 0, margin: 0 },
  companyRow: {
    display: "flex",
    alignItems: "center",
    padding: "6px 0",
    borderBottom: "1px solid #eee",
    fontSize: 13
  },
  companyName: { fontWeight: 500 },
  productLines: { color: "#666", fontSize: 12, marginLeft: 12 },
  loading: { padding: 40, textAlign: "center", color: "#888" }
};

const Pill = ({ active }) => (
  <span style={{ ...styles.pill, ...(active ? styles.pillActive : styles.pillInactive) }}>
    {active ? "Active" : "Inactive"}
  </span>
);
Pill.propTypes = { active: PropTypes.bool.isRequired };

const teamShape = PropTypes.shape({
  pkiTeamID: PropTypes.number.isRequired,
  sTeamName: PropTypes.string.isRequired,
  bActive: PropTypes.bool.isRequired,
  sOutboundPhoneNumber: PropTypes.string,
  teamLeadName: PropTypes.string,
  tlaName: PropTypes.string,
  followUpCoachNames: PropTypes.arrayOf(PropTypes.string),
  activeEmployeeCount: PropTypes.number,
  companies: PropTypes.arrayOf(PropTypes.shape({
    pkiCompany: PropTypes.number.isRequired,
    sName: PropTypes.string,
    bActive: PropTypes.bool.isRequired,
    productLines: PropTypes.arrayOf(PropTypes.string)
  }))
});

const activeCompanyCount = team => (team.companies || []).filter(c => c.bActive).length;

const TeamRow = ({ team, expanded, onToggleExpanded, onToggleActive, saving, confirming, onRequestConfirm, onCancelConfirm }) => {
  const blockingCount = activeCompanyCount(team);
  const deactivateBlocked = team.bActive && blockingCount > 0;
  const formattedPhone = formatPhone(team.sOutboundPhoneNumber || "");
  const showActivateButton = !team.bActive;

  const metaParts = [];
  if (team.teamLeadName) metaParts.push(`Lead: ${team.teamLeadName}`);
  if (team.tlaName)      metaParts.push(`TLA: ${team.tlaName}`);
  if (formattedPhone)    metaParts.push(formattedPhone);

  return (
    <div style={styles.row}>
      <div
        style={{ ...styles.rowHeader, ...(team.bActive ? {} : styles.rowHeaderInactive) }}
        onClick={onToggleExpanded}>
        <span style={styles.caret}>{expanded ? "▼" : "▶"}</span>
        <span style={{ ...styles.teamName, ...(team.bActive ? {} : styles.teamNameInactive) }}>
          {team.sTeamName}
        </span>
        {metaParts.length > 0 && (
          <span style={styles.teamMeta}>{metaParts.join(" · ")}</span>
        )}
        <span style={styles.spacer} />
        {team.bActive && blockingCount > 0 && (
          <span style={{ ...styles.pill, ...styles.pillWarn }}>
            {blockingCount} active {blockingCount === 1 ? "company" : "companies"}
          </span>
        )}
        <Pill active={team.bActive} />
      </div>
      {expanded && (
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <span style={styles.panelHeaderText}>
              <strong>{team.activeEmployeeCount ?? 0}</strong> active {team.activeEmployeeCount === 1 ? "employee" : "employees"} on this team
            </span>
            {team.bActive && !confirming && (
              <button
                style={deactivateBlocked || saving ? styles.buttonDisabled : styles.button}
                disabled={deactivateBlocked || saving}
                title={
                  deactivateBlocked
                    ? `Cannot deactivate — ${blockingCount} ${blockingCount === 1 ? "company assigned to this team is" : "companies assigned to this team are"} still active. Deactivate or reassign ${blockingCount === 1 ? "it" : "them"} first.`
                    : "Deactivate this team"
                }
                onClick={() => {
                  if (deactivateBlocked || saving) return;
                  onRequestConfirm(team);
                }}>
                {saving ? "Saving…" : "Deactivate"}
              </button>
            )}
            {team.bActive && confirming && (
              <>
                <button
                  style={styles.buttonDanger}
                  disabled={saving}
                  onClick={() => onToggleActive(team)}>
                  {saving ? "Saving…" : "Confirm deactivate"}
                </button>
                <button
                  style={styles.cancelButton}
                  disabled={saving}
                  onClick={() => onCancelConfirm(team)}>
                  Cancel
                </button>
              </>
            )}
            {showActivateButton && (
              <button
                style={styles.button}
                disabled={saving}
                title="Reactivate this team"
                onClick={() => onToggleActive(team)}>
                {saving ? "Saving…" : "Activate"}
              </button>
            )}
          </div>
          {(team.followUpCoachNames || []).length > 0 && (
            <>
              <div style={styles.panelSubheader}>Follow-up coaches</div>
              <div style={styles.chipRow}>
                {team.followUpCoachNames.map(name => (
                  <span key={name} style={styles.chip}>{name}</span>
                ))}
              </div>
            </>
          )}
          <div style={styles.panelSubheader}>Companies assigned to this team</div>
          {(team.companies || []).length === 0 ? (
            <div style={styles.meta}>No companies assigned to this team.</div>
          ) : (
            <ul style={styles.companyList}>
              {team.companies.map(c => (
                <li key={c.pkiCompany} style={styles.companyRow}>
                  <span style={styles.companyName}>{c.sName}</span>
                  <Pill active={c.bActive} />
                  {(c.productLines || []).length > 0 && (
                    <span style={styles.productLines}>{c.productLines.join(" · ")}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
TeamRow.propTypes = {
  team: teamShape.isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggleExpanded: PropTypes.func.isRequired,
  onToggleActive: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  confirming: PropTypes.bool.isRequired,
  onRequestConfirm: PropTypes.func.isRequired,
  onCancelConfirm: PropTypes.func.isRequired
};

const TeamInfo = () => {
  const dispatch = useDispatch();
  const [teams, setTeams] = useState(null);
  const [expanded, setExpanded] = useState({}); // { [pkiTeamID]: true }
  const [saving, setSaving] = useState({});     // { [pkiTeamID]: true }
  const [confirming, setConfirming] = useState({}); // { [pkiTeamID]: true }
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await dispatch(fetchTeams());
      setTeams(prev => result ?? prev ?? []);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => { load(); }, [load]);

  const onToggleExpanded = id =>
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const onRequestConfirm = team =>
    setConfirming(prev => ({ ...prev, [team.pkiTeamID]: true }));

  const onCancelConfirm = team =>
    setConfirming(prev => ({ ...prev, [team.pkiTeamID]: false }));

  const onToggleActive = async team => {
    const target = !team.bActive;
    setSaving(prev => ({ ...prev, [team.pkiTeamID]: true }));
    try {
      const result = await dispatch(setTeamActive(team.pkiTeamID, target));
      if (result?.success) {
        setConfirming(prev => ({ ...prev, [team.pkiTeamID]: false }));
        await load();
      }
    } finally {
      setSaving(prev => ({ ...prev, [team.pkiTeamID]: false }));
    }
  };

  return (
    <HasRolePermission allowedRoles={["Administrator"]}>
      <div style={styles.page}>
        <PageHeader title="Team Info" />
        <div style={styles.blurb}>{PHONE_BLURB}</div>
        <div style={styles.toolbar}>
          <span style={styles.meta}>
            {teams ? `${teams.length} teams (${teams.filter(t => t.bActive).length} active)` : ""}
          </span>
          <button style={styles.button} onClick={load} disabled={loading}>
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
        {loading && !teams && <div style={styles.loading}>Loading teams…</div>}
        {teams && teams.length === 0 && !loading && (
          <div style={styles.loading}>No teams found.</div>
        )}
        {teams && teams.map(team => (
          <TeamRow
            key={team.pkiTeamID}
            team={team}
            expanded={!!expanded[team.pkiTeamID]}
            onToggleExpanded={() => onToggleExpanded(team.pkiTeamID)}
            onToggleActive={onToggleActive}
            saving={!!saving[team.pkiTeamID]}
            confirming={!!confirming[team.pkiTeamID]}
            onRequestConfirm={onRequestConfirm}
            onCancelConfirm={onCancelConfirm}
          />
        ))}
      </div>
    </HasRolePermission>
  );
};

export default TeamInfo;
